import { useCallback, useEffect, useRef, useState } from 'react';
import { post } from '@/utils/api';

type MintResponse = { code: string; expires_in: number };

function formatCountdown(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function TaskpaneLink() {
  const [code, setCode] = useState<string>('');
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const mint = useCallback(() => {
    post<MintResponse>('auth/device/mint', {})
      .then((res) => {
        setCode(res.code);
        setCountdown(res.expires_in);
        setError('');
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to generate code'
        );
      });
  }, []);

  useEffect(() => {
    mint();
  }, [mint]);

  useEffect(() => {
    if (!code) return undefined;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [code]);

  useEffect(() => {
    if (countdown !== 0 || !code) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCode('');
    mint();
  }, [countdown, code, mint]);

  function handleCopy() {
    if (!code) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // clipboard unavailable — user will copy manually
      });
  }

  const statusText = () => {
    if (!code) return 'Generating code…';
    if (countdown > 0) return `Expires in ${formatCountdown(countdown)}`;
    return 'Generating new code…';
  };

  return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: 24 }}>
      <h2>Connect Excel taskpane</h2>
      <p>
        Paste this code into the Sign in screen of the OpStart taskpane in
        Excel. The code is one-shot and tied to your account.
      </p>

      <div
        style={{
          fontSize: 28,
          letterSpacing: 6,
          textAlign: 'center',
          padding: '18px 12px',
          margin: '20px 0 8px',
          background: '#eef1f6',
          border: '1px solid #c5cbd6',
          borderRadius: 6,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontWeight: 700,
          color: '#111827',
        }}
      >
        {code || '…'}
      </div>

      <div
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: countdown <= 30 ? '#b91c1c' : '#6b7280',
          marginBottom: 16,
        }}
      >
        {statusText()}
      </div>

      <button
        type='button'
        className='btn btn-accent'
        onClick={handleCopy}
        disabled={!code}
        style={{ width: '100%' }}
      >
        {copied ? 'Copied! ✓' : 'Copy code'}
      </button>

      {error && (
        <div style={{ marginTop: 12 }}>
          <p style={{ color: '#b91c1c', margin: '0 0 8px' }}>{error}</p>
          <button
            type='button'
            className='btn'
            onClick={mint}
            style={{ width: '100%' }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
